import { useCallback, useEffect, useState } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import dayjs from 'dayjs';
import { useQuery } from 'react-query';
import { AudioLibraryApi, AudioLibraryTypes } from '@frontend/api-audio-library';
import { getJTI, hasSchemaACL } from '@frontend/auth-helpers';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useInvalidateQueries } from '@frontend/location-helpers';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { DownloadIcon, PrimaryButton, Table, Text, useModalControl } from '@frontend/design-system';
import { AudioFileDeleteModal } from '../../components/audio-library/delete-button';
import { NewEditMediaName } from '../../components/audio-library/edit-media-name.new';
import { AudioLibraryNotificationRibbon } from '../../components/audio-library/notification-ribbon';
import { AudioUploadModal } from '../../components/audio-picker/audio-upload-modal';
import { CachedAudioScrubber } from '../../components/common/cached-audio-scrubber';
import { useMediaUploadModal } from '../../components/media-picker';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { queryKeys } from '../../query-keys';
import { trackingId } from '../../tracking';
import { buildAudioLibraryPath } from '../../utils/media-path';

export const AudioLibrarySettings = () => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const [searchValue, setSearchValue] = useState('');
  const jti = getJTI();
  const { triggerProps, modalProps } = useMediaUploadModal();
  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();
  const [showRibbon, setShowRibbon] = useState(true);
  const { isSingleTypeScope } = useAppScopeStore();
  const isMultiLocation = !isSingleTypeScope;
  const ribbonClosedKey = `ribbonClosed-${settingsTenantLocation?.id}`;
  const invalidateQueries = useInvalidateQueries();
  const permissionBase = settingsTenantLocation?.id;
  const hasPhoneFullAccess = permissionBase ? hasSchemaACL(permissionBase, Permission.PHONE_FULL_ACCESS) : false;

  const { data, isLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.audioLibrary()],
    queryFn: () =>
      AudioLibraryApi.listAudioLibrary({ tenantId: settingsTenantLocation?.phoneTenantId ?? '' }).then(
        (res) => res.audioFiles
      ),
    select: (data) => data?.sort((a, b) => a.name.localeCompare(b.name)),
    enabled: !!settingsTenantLocation?.phoneTenantId,
    // Currently users can add audio files by calling *86 on their phones. They then expect to see the audio file in the list
    // immediately. This is a temporary solution to reduce some of the friction until we can implement a websocket solution to
    // update the list in real time.
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: true,
    meta: {
      error: {
        message: 'AudioLibrarySettings:listAudioLibraryQuery | Failed to fetch audio library',
        data: {
          tenantId: settingsTenantLocation?.phoneTenantId,
          hasPhoneFullAccess,
          isMultiLocation,
          isSingleTypeScope,
        },
      },
    },
  });

  const [filteredMedia, setFilteredMedia] = useState<AudioLibraryTypes.AudioFileType[]>(data ?? []);

  useEffect(() => {
    const filteredData =
      data?.filter((item: { name: string }) => item.name.toLowerCase().includes(searchValue.toLowerCase())) ?? [];
    setFilteredMedia(filteredData);
  }, [searchValue, data]);

  const handleDownloadClick = async (filePath: string, fileName: string) => {
    const response = await fetch(filePath, {
      headers: {
        Authorization: `Bearer ${jti}`,
      },
    });
    const blob = await response.blob();
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(link.href);
    document.body.removeChild(link);
  };

  const [deleteRowData, setDeleteRowData] = useState<AudioLibraryTypes.AudioFileType>();

  const handleDeleteClick = (row: AudioLibraryTypes.AudioFileType) => {
    deleteTriggerProps.onClick();
    setDeleteRowData(row);
  };

  useEffect(() => {
    const isRibbonClosed = sessionStorage.getItem(ribbonClosedKey) === 'true';
    setShowRibbon(!isRibbonClosed);
  }, [data]);

  // sessionStorage is cleared upon page reload/refresh
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.clear();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleRibbonClose = useCallback(() => {
    setShowRibbon(false);
    sessionStorage.setItem(ribbonClosedKey, 'true');
  }, [ribbonClosedKey, setShowRibbon]);

  return (
    <>
      <PhoneSettingsPage
        ribbon={showRibbon && isMultiLocation && <AudioLibraryNotificationRibbon onClose={handleRibbonClose} />}
        title={t('Audio Library')}
        subtitle={t(
          'Manage all of the audio files used by your Weave phone system such as: Voicemail Greetings, Phone Tree, Voicemail Override (VMO), Hold Music, etc.'
        )}
        breadcrumbs={[
          { label: t('Phone'), to: '/phone/main' },
          { label: t('Audio Library'), to: '/phone/audio-library' },
        ]}
        action={
          <PrimaryButton
            size='large'
            {...triggerProps}
            trackingId={trackingId({ context: 'setting', feature: 'audio-library', details: 'add-audio-btn' })}
          >
            {t('Add Audio File')}
          </PrimaryButton>
        }
      >
        {settingsTenantLocation ? (
          <Table
            emptyStateConfig={{
              type: 'sync_your_phone',
              header: t('No Audio'),
              description: () =>
                !!data?.length ? (
                  <Text textAlign='center'>{t('There are no Audio Files to display with this filter setting.')}</Text>
                ) : (
                  <Trans t={t}>
                    <Text textAlign='center'>You don't have any audio files yet. </Text>
                    <Text textAlign='center'>
                      Click{' '}
                      <Text as='span' weight='bold'>
                        Add Audio File
                      </Text>{' '}
                      to get started.
                    </Text>
                  </Trans>
                ),
            }}
            colConfig={[
              {
                Header: t('File Name'),
                accessor: (media) => media,
                cellRenderer: (media) => <NewEditMediaName phoneMedia={media} editEnabled={true} />,
                id: 'mediaName',
                minWidth: 280,
                sortDescFirst: false,
              },
              {
                Header: t('Uploaded at'),
                id: 'updatedAt',
                accessor: (media) => media.updatedAt,
                cellRenderer: (createdAt) => dayjs(createdAt).format('MMM D, YYYY h:mm A'),
              },
              {
                Header: t('Audio'),
                id: 'audio',
                accessor: (media) => media,
                cellRenderer: ({ mediaItemId, downloadUrl }) => {
                  return (
                    <CachedAudioScrubber
                      filePath={buildAudioLibraryPath({
                        media: { id: mediaItemId, path: downloadUrl, isGlobal: false },
                      })}
                      mediaId={mediaItemId}
                    />
                  );
                },
                disableSortBy: true,
              },
            ]}
            isLoading={isLoading}
            hasResponsiveColWidths
            hasGlobalSearch
            globalSearchConfig={{
              searchHandler: setSearchValue,
              placeholder: t('Search'),
              debounceDelay: 500,
            }}
            data={filteredMedia}
            rowActions={{
              actions: [
                {
                  Icon: DownloadIcon,
                  label: t('Download Audio File'),
                  onClick: (row) => handleDownloadClick(row.downloadUrl ?? '', row.name),
                  trackingId: trackingId({
                    context: 'setting',
                    feature: 'audio-library',
                    details: 'download-audio-btn',
                  }),
                },
                {
                  label: <span css={{ color: theme.colors.critical50 }}>{t('Delete Audio File')}</span>,
                  Icon: () => <Icon name='trash' color='error' size={16} />,
                  onClick: (row) => handleDeleteClick(row),
                  trackingId: trackingId({ context: 'setting', feature: 'audio-library', details: 'delete-audio-btn' }),
                },
              ],
            }}
            isPaginated
          ></Table>
        ) : (
          <div>{t('No Phone Tenant Selected')}</div>
        )}
        {settingsTenantLocation && (
          <AudioUploadModal
            modalProps={modalProps}
            onSuccess={() => invalidateQueries(queryKeys.settings.audioLibrary())}
          />
        )}
        {settingsTenantLocation && deleteRowData && (
          <AudioFileDeleteModal
            phoneTenantId={settingsTenantLocation.phoneTenantId}
            name={deleteRowData.name}
            id={deleteRowData.mediaItemId}
            modalProps={deleteModalProps}
            disableLinks={!hasPhoneFullAccess}
          />
        )}
      </PhoneSettingsPage>
    </>
  );
};
